import createReducer from 'redux/createReducer';

import type { BasketBuilderExperienceSuggestionsResponse } from 'api/definitions/basket-builder-experience/types';
import type { Pricing2PricedProductsResponse } from 'api/definitions/pricing';
import type { TransformedBasketBuilderExperienceSuggestionsResponse } from 'redux/transforms/transform-basket-builder-suggestions-into-legacy-format';
import type { LegacyProductReference } from 'constants/products';

import {
  GET_QUICK_SHOP_SUGGESTIONS,
  GET_ESTIMATED_PRICE,
} from 'redux/modules/quick-shop/actions/types';

import type {
  QuickShopState,
  QuickShopRequestState,
  QuickShopSuccessState,
} from 'redux/modules/quick-shop/types';

import { initialState } from '../state';

export type BasketBuilderExperienceSuggestionsFetcherResult = Pick<
  TransformedBasketBuilderExperienceSuggestionsResponse<BasketBuilderExperienceSuggestionsResponse>,
  'eligibility' | 'metadata' | 'recommendedSelection'
> & {
  suggestions: LegacyProductReference[];
};

export type BasketBuilderExperienceSuggestionsSuccessAction = {
  result: BasketBuilderExperienceSuggestionsFetcherResult;
};

export type EstimatedPriceSuccessAction = {
  result: Pricing2PricedProductsResponse;
};

const getBasketBuilderExperienceSuggestionsRequest = (): QuickShopRequestState => {
  return {
    loading: true,
  };
};

const getBasketBuilderExperienceSuggestionsSuccess = (
  state: QuickShopState,
  {
    result: { eligibility, suggestions, metadata, recommendedSelection },
  }: BasketBuilderExperienceSuggestionsSuccessAction,
): QuickShopSuccessState => {
  return {
    ...state,
    loading: false,
    eligibility,
    suggestions,
    recommendedSelection,
    hasNextPage: metadata.hasNextPage,
    totalResults: metadata.totalResults,
    start: metadata.start,
    size: metadata.size,
    allSuggestionsInTrolley: metadata.allSuggestionsInTrolley,
    anySuggestionsInTrolley: metadata.anySuggestionsInTrolley,
  };
};

const getEstimatedPriceRequest = (state: QuickShopState) => {
  return {
    ...state,
    estimatedPriceLoading: true,
  };
};

const getEstimatedPriceSuccess = (
  state: QuickShopState,
  {
    result: [
      {
        totals: {
          totalPrice: { amount },
        },
      },
    ],
  }: EstimatedPriceSuccessAction,
) => {
  return {
    ...state,
    estimatedPriceLoading: false,
    estimatedPrice: amount,
  };
};

const getEstimatedPriceFailure = (state: QuickShopState) => {
  return {
    ...state,
    estimatedPriceLoading: false,
    estimatedPrice: undefined,
  };
};

const actionTypeReducerMap = [
  [GET_QUICK_SHOP_SUGGESTIONS.request, getBasketBuilderExperienceSuggestionsRequest],
  [GET_QUICK_SHOP_SUGGESTIONS.success, getBasketBuilderExperienceSuggestionsSuccess],
  [GET_ESTIMATED_PRICE.request, getEstimatedPriceRequest],
  [GET_ESTIMATED_PRICE.success, getEstimatedPriceSuccess],
  [GET_ESTIMATED_PRICE.failure, getEstimatedPriceFailure],
] as const;

export type PayloadMap = ReducerMapTupleToRecord<typeof actionTypeReducerMap>;
export default createReducer(initialState, actionTypeReducerMap);
